'use client';
import { FC, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';

import Text from '@/components/Text';
import Button from '@/components/Button';
import { TextField, CheckboxNew } from '@/components/Inputs';
import useToggle from '@/hooks/useToggle';
import { storage } from '@/config';
import { ComponentPropTypes } from './LoginSignupFlow';
import { useUser } from '@/context/userContext';
import { useCleverTap } from '@/context/clevertapContext';
import { saveProfileAction, updateGuestUserCartAction } from '@/app/actions';
import { useCart } from '@/context/cartContext';

type FormTypes = {
  firstName: string;
  lastName: string;
  email: string;
  isSubscribed: boolean;
};

type PropTypes = ComponentPropTypes;

const PersonalDetails: FC<PropTypes> = ({ onClose, loginRedirect, source }) => {
  const router: any = useRouter();
  const { value: isSubmitting, toggleValue: setSubmitting } = useToggle();
  const { updateUserDetails } = useUser();
  const { pushEvent } = useCleverTap();
  const { fetchUpdateCart } = useCart();

  const FormSchema = yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .required('First Name field cannot be blank.')
      .matches(
        /^[A-Za-z]+$/,
        'Special characters and numbers are not allowed in the First Name field.'
      ),
    lastName: yup
      .string()
      .trim()
      .required('Last Name field cannot be blank.')
      .matches(
        /^[A-Za-z]+$/,
        'Special characters and numbers are not allowed in the Last Name field.'
      ),
    email: yup
      .string()
      .trim()
      .required('Email field cannot be left blank.')
      .email('Invalid email format. Eg: test@example.com'),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormTypes>({
    resolver: yupResolver(FormSchema) as any,
  });

  const onSubmit = handleSubmit(async values => {
    const mobilenumber = storage.get('mobile_number');
    setSubmitting(true);
    if (values) {
      const stringRes = await saveProfileAction(values);
      const parsedRes = JSON.parse(stringRes);
      if (parsedRes?.success) {
        pushEvent({
          event: 'Sign Up',
          data: {
            'First Name': values.firstName,
            'Last Name': values.lastName,
            'Customer Mobile': mobilenumber,
            Email: values.email,
            'Receive Communication': values.isSubscribed,
            'Method Used': 'OTP',
            Status: 'Success',
          },
        });
        storage.set('token', parsedRes?.auth_token);
        const cartId = storage.get('cart_id') ?? '';
        if (cartId) {
          const stringRes =
            (await updateGuestUserCartAction({
              customerId: parsedRes?.userDetails?.customer_id,
              storeId: parsedRes?.userDetails?.store_id,
            })) ?? '';
          const parsedResUpdateCart = JSON.parse(stringRes);
          if (parsedResUpdateCart?.error) {
            toast.error(parsedResUpdateCart?.error?.message);
          } else {
            storage.remove('cart_id');
            storage.set('quote_id', parsedResUpdateCart?.quote_id);
          }
        }
        fetchUpdateCart();
        await updateUserDetails({ token: parsedRes?.auth_token });
        if (loginRedirect && loginRedirect !== '') {
          setSubmitting(false);
          router.push(loginRedirect);
        }
        toast.success(parsedRes?.message);
        onClose();
      } else {
        pushEvent({
          event: 'Sign Up',
          data: { 'Register Option': 'phone', Status: 'failure' },
        });
        toast.error(parsedRes?.message);
      }
    }
    setSubmitting(false);
  });

  useEffect(() => {
    const data: any = {
      'Page URL': window.location.href,
      'Page Name': document.title,
      Step: 'Enter Personal Details',
    };
    if (source) {
      data['Source'] = source;
    }
    pushEvent({
      event: 'Login Flow',
      data,
    });
  }, []);

  return (
    <>
      <Text className='p-0'>
        We’d love to learn more about you. To update & edit details, go to the
        ‘My account’ section
      </Text>
      <form
        name='personal-details-form'
        onSubmit={onSubmit}
        className='space-y-3'>
        <div className='flex gap-x-2'>
          <div className='flex-1'>
            <TextField
              id='firstName'
              placeholder='First Name*'
              register={register}
              error={errors?.firstName}
            />
          </div>
          <div className='flex-1'>
            <TextField
              id='lastName'
              placeholder='Last Name*'
              register={register}
              error={errors?.lastName}
            />
          </div>
        </div>
        <TextField
          id='email'
          placeholder='Email Address*'
          register={register}
          error={errors?.email}
        />
        <div className='flex items-start gap-x-2'>
          <CheckboxNew
            id='isSubscribed'
            register={register}
            defaultChecked={true}
            label='I agree to receive notifications and updates over WhatsApp and Email.'
          />
        </div>
        <Button
          className='mt-2'
          type='submit'
          label={isSubmitting ? 'Please wait....' : `Let's Go`}
          disabled={isSubmitting}
        />
      </form>
    </>
  );
};

export default PersonalDetails;
